<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <h6 class="text-h6 font-weight-bold">
                      {{ $t("title.edit_group_papers") }}
                    </h6>
                    <p class="text-body-2 mb-0">
                      {{ $t("txt.edit_group_papers_description") }}
                    </p>
                  </div>
                  <v-btn
                    color="indigo"
                    outlined
                    @click="openFullJournalPreview"
                  >
                    <v-progress-circular
                      v-if="isFullPdfLoading"
                      indeterminate
                      color="indigo"
                      size="20"
                      width="2"
                      class="mr-2"
                    ></v-progress-circular>
                    <v-icon v-else left size="18">mdi-book-open-page-variant</v-icon>
                    전체 미리보기
                  </v-btn>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <!-- 좌측: 필수 페이지 + 논문 목록 -->
              <v-col cols="12" xl="3" lg="3" md="4" sm="12">
                <v-card class="rounded-lg overflow-y-auto" style="height: calc(100vh - 180px); min-height: 700px;" outlined>
                  <v-card-text>
                    <!-- 필수 페이지 (고정) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-2">
                        <p class="text-caption grey--text mb-0 font-weight-bold">필수 페이지</p>
                        <v-btn x-small text color="primary" @click="saveFixedPagesSettings" :loading="isSavingSettings">
                          <v-icon x-small left>mdi-content-save</v-icon>
                          설정 저장
                        </v-btn>
                      </div>
                      <v-list dense class="pa-0">
                        <v-list-item
                          v-for="fixedItem in fixedPages"
                          :key="fixedItem.id"
                          @click="selectFixedPage(fixedItem)"
                          :class="{
                            'primary lighten-5': selectedType === 'fixed' && selectedFixedPage?.id === fixedItem.id,
                            'grey lighten-4': !fixedItem.enabled,
                          }"
                          class="rounded-lg mb-2"
                          link
                        >
                          <v-list-item-avatar>
                            <v-sheet
                              :color="fixedItem.enabled ? fixedItem.color : 'grey lighten-1'"
                              class="rounded-lg d-flex align-center justify-center"
                              width="40"
                              height="40"
                            >
                              <v-icon color="white" size="20">{{ fixedItem.icon }}</v-icon>
                            </v-sheet>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="text-body-2 font-weight-medium" :class="{ 'grey--text': !fixedItem.enabled }">
                              {{ fixedItem.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                              {{ fixedItem.description }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action class="flex-row align-center" style="gap: 4px;">
                            <v-switch
                              v-model="fixedItem.enabled"
                              dense
                              hide-details
                              class="mt-0 pt-0"
                              @click.stop
                              :label="fixedItem.enabled ? '사용' : '사용안함'"
                            ></v-switch>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <!-- 논문 목록 -->
                    <div>
                      <p class="text-caption grey--text mb-2 font-weight-bold">논문 목록</p>
                      <div v-if="papers.length === 0" class="text-center pa-4">
                        <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
                        <p class="text-body-2 grey--text mt-2">
                          {{ $t("txt.no_submitted_papers") }}
                        </p>
                      </div>

                      <v-list v-else dense class="pa-0">
                        <v-list-item
                          v-for="(paper, index) in papers"
                          :key="paper.idx"
                          @click="selectPaper(paper)"
                          :class="{
                            'primary lighten-5': selectedType === 'paper' && selectedPaper?.idx === paper.idx,
                          }"
                          class="rounded-lg mb-2"
                          link
                        >
                          <v-list-item-avatar>
                            <v-sheet
                              color="grey darken-4"
                              class="rounded-lg d-flex align-center justify-center"
                              width="40"
                              height="40"
                            >
                              <span class="white--text text-body-2 font-weight-bold">
                                {{ index + 1 }}
                              </span>
                            </v-sheet>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="text-body-2 font-weight-medium">
                              {{ paper.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle
                              v-if="paper.title_eng"
                              class="text-caption"
                            >
                              {{ paper.title_eng }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle class="text-caption mt-1">
                              <v-chip x-small outlined color="primary">
                                {{ paper.status_label || $t("label.submitted") }}
                              </v-chip>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-icon
                              size="16"
                              :color="selectedType === 'paper' && selectedPaper?.idx === paper.idx ? 'primary' : 'grey'"
                            >
                              mdi-chevron-right
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- 우측: 에디터 영역 -->
              <v-col cols="12" xl="9" lg="9" md="8" sm="12">
                <v-card class="rounded-lg" style="height: calc(100vh - 180px); min-height: 700px;" outlined>
                  <!-- 아무것도 선택 안됨 -->
                  <v-card-text v-if="!selectedType" class="fill-height">
                    <div class="d-flex flex-column justify-center align-center fill-height">
                      <v-icon size="64" color="grey lighten-1">mdi-file-edit-outline</v-icon>
                      <p class="text-body-1 grey--text mt-4">
                        편집할 항목을 선택하세요
                      </p>
                    </div>
                  </v-card-text>

                  <!-- 고정 페이지 에디터 -->
                  <v-card-text v-else-if="selectedType === 'fixed'" class="overflow-y-auto" style="height: calc(100vh - 220px); min-height: 650px;">
                    <!-- 표지 편집 -->
                    <div v-if="selectedFixedPage?.id === 'cover'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">표지 정보</h6>
                        <div class="d-flex">
                          <v-btn color="primary" outlined class="mr-2" @click="showFixedPreviewModal = true">
                            <v-icon left size="18">mdi-eye</v-icon>
                            미리보기
                          </v-btn>
                          <v-btn color="success" @click="handleSaveJournalInfo" :loading="isLoading">
                            <v-icon left size="18">mdi-content-save</v-icon>
                            저장
                          </v-btn>
                        </div>
                      </div>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="coverData.journal_title"
                            label="저널 제목"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="coverData.journal_title_eng"
                            label="저널 제목 (영문)"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="coverData.issn"
                            label="ISSN"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="coverData.society_name"
                            label="학회명"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="coverData.society_name_eng"
                            label="학회명 (영문)"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- 정보페이지 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'info'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">정보페이지 (임원/발행 정보)</h6>
                        <div class="d-flex">
                          <v-btn color="primary" outlined class="mr-2" @click="showFixedPreviewModal = true">
                            <v-icon left size="18">mdi-eye</v-icon>
                            미리보기
                          </v-btn>
                          <v-btn color="success" @click="handleSaveInfoPage" :loading="isLoading">
                            <v-icon left size="18">mdi-content-save</v-icon>
                            저장
                          </v-btn>
                        </div>
                      </div>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.advisor" label="고문" outlined rows="3" hint="쉼표로 구분" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.advisory_committee" label="자문위원" outlined rows="3" hint="쉼표로 구분" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.president" label="회장" outlined rows="3" hint="이름(소속)" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.vice_president" label="부회장" outlined rows="3" hint="쉼표로 구분" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.secretary_general" label="사무총장" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.general_affairs" label="총무이사" outlined rows="3" hint="쉼표로 구분" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.research_planning" label="연구이사" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.information" label="정보이사" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.editing" label="편집이사" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.public_information" label="홍보이사" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.international_cooperation" label="국제협력이사" outlined rows="3"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field v-model="infoPageData.editorial_board_chair" label="편집위원장" outlined dense></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="infoPageData.editorial_board" label="편집위원" outlined rows="3"></v-textarea>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- 백커버 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'backcover'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">백커버 (논문 제목/저자)</h6>
                        <v-btn color="primary" outlined @click="showFixedPreviewModal = true">
                          <v-icon left size="18">mdi-eye</v-icon>
                          미리보기
                        </v-btn>
                      </div>
                      <v-alert type="info" text outlined class="mb-4">
                        백커버는 등록된 논문의 제목과 저자를 자동으로 표시합니다. 논문 정보를 수정하면 백커버에 반영됩니다.
                      </v-alert>
                      <v-card outlined class="pa-4">
                        <p class="text-subtitle-2 font-weight-bold mb-3">현재 등록된 논문 ({{ papers.length }}편)</p>
                        <v-list dense v-if="papers.length > 0">
                          <v-list-item v-for="(paper, index) in papers" :key="paper.idx" class="px-0">
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                {{ index + 1 }}. {{ paper.title }}
                              </v-list-item-title>
                              <v-list-item-subtitle v-if="paper.title_eng" class="text-caption">
                                {{ paper.title_eng }}
                              </v-list-item-subtitle>
                              <v-list-item-subtitle class="text-caption mt-1">
                                {{ formatAuthors(paper.authors) }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        <p v-else class="text-body-2 grey--text">등록된 논문이 없습니다.</p>
                      </v-card>
                    </div>

                    <!-- 속표지(앞) 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'inner_front'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">속표지(앞)</h6>
                        <v-btn color="primary" outlined @click="showFixedPreviewModal = true">
                          <v-icon left size="18">mdi-eye</v-icon>
                          미리보기
                        </v-btn>
                      </div>
                      <v-alert type="info" text outlined class="mb-4">
                        속표지(앞)는 표지와 동일한 디자인입니다. 표지 정보를 수정하면 속표지(앞)에도 반영됩니다.
                      </v-alert>
                    </div>

                    <!-- 빈페이지 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'blank'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">빈페이지 (공페이지)</h6>
                        <v-btn color="primary" outlined @click="showFixedPreviewModal = true">
                          <v-icon left size="18">mdi-eye</v-icon>
                          미리보기
                        </v-btn>
                      </div>
                      <v-alert type="info" text outlined class="mb-4">
                        빈 페이지입니다. 별도의 편집 내용이 없습니다.
                      </v-alert>
                    </div>

                    <!-- 표제지 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'title'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">표제지 (Editor in Chief / Editorial Board)</h6>
                        <div class="d-flex">
                          <v-btn color="primary" outlined class="mr-2" @click="showFixedPreviewModal = true">
                            <v-icon left size="18">mdi-eye</v-icon>
                            미리보기
                          </v-btn>
                          <v-btn color="success" @click="handleSaveTitlePage" :loading="isLoading">
                            <v-icon left size="18">mdi-content-save</v-icon>
                            저장
                          </v-btn>
                        </div>
                      </div>
                      <v-row dense>
                        <v-col cols="12">
                          <v-textarea
                            v-model="titlePageData.editorial_board_chair"
                            label="Editor in Chief (편집위원장)"
                            outlined
                            rows="4"
                            hint="한 줄에 한 명씩 입력 (예: Michael Namkil Kim, University of Southern California, USA)"
                            persistent-hint
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="titlePageData.editorial_board"
                            label="Editorial Board (편집위원)"
                            outlined
                            rows="8"
                            hint="한 줄에 한 명씩 입력 (예: Kyungho Kim, Mejiro University, Japan)"
                            persistent-hint
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- 목차 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'toc'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">목차 (CONTENTS)</h6>
                        <v-btn color="primary" outlined @click="showFixedPreviewModal = true">
                          <v-icon left size="18">mdi-eye</v-icon>
                          미리보기
                        </v-btn>
                      </div>
                      <v-alert type="info" text outlined class="mb-4">
                        목차는 등록된 논문 목록을 기반으로 자동 생성됩니다. 논문 정보를 수정하면 목차에 반영됩니다.
                      </v-alert>
                      <v-card outlined class="pa-4">
                        <p class="text-subtitle-2 font-weight-bold mb-3">현재 등록된 논문 ({{ papers.length }}편)</p>
                        <v-list dense v-if="papers.length > 0">
                          <v-list-item v-for="(paper, index) in papers" :key="paper.idx" class="px-0">
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                {{ index + 1 }}. {{ paper.title }}
                              </v-list-item-title>
                              <v-list-item-subtitle v-if="paper.title_eng" class="text-caption">
                                {{ paper.title_eng }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        <p v-else class="text-body-2 grey--text">등록된 논문이 없습니다.</p>
                      </v-card>
                    </div>

                    <!-- 속표지(뒷) 편집 -->
                    <div v-else-if="selectedFixedPage?.id === 'inner_back'">
                      <div class="d-flex justify-space-between align-center mb-4">
                        <h6 class="text-subtitle-1 font-weight-bold">속표지(뒷) - 임원 정보</h6>
                        <div class="d-flex">
                          <v-btn color="primary" outlined class="mr-2" @click="showFixedPreviewModal = true">
                            <v-icon left size="18">mdi-eye</v-icon>
                            미리보기
                          </v-btn>
                          <v-btn color="success" @click="handleSaveInnerBack" :loading="isLoading">
                            <v-icon left size="18">mdi-content-save</v-icon>
                            저장
                          </v-btn>
                        </div>
                      </div>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.advisory_committee" label="Advisory Committee" outlined rows="4" hint="한 줄에 한 명씩 입력" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.president" label="President" outlined rows="4" hint="한 줄에 한 명씩 입력" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.vice_president" label="Vice President" outlined rows="4" hint="한 줄에 한 명씩 입력" persistent-hint></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.secretary_general" label="Secretary General" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.general_affairs" label="General Affairs" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.research_planning" label="Research Planning" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.information" label="Information" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.editing" label="Editing" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.public_information" label="Public Information" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-textarea v-model="innerBackData.public_relations" label="Public Relations" outlined rows="4"></v-textarea>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea v-model="innerBackData.editorial_board" label="Editorial Board" outlined rows="4"></v-textarea>
                        </v-col>
                      </v-row>
                    </div>
                  </v-card-text>

                  <!-- 논문 에디터 -->
                  <v-card-text v-else-if="selectedType === 'paper' && selectedPaper" class="pa-0" style="height: calc(100vh - 220px); min-height: 650px;">
                    <!-- 상단 툴바 -->
                    <div class="d-flex justify-space-between align-center pa-4 border-bottom">
                      <div class="d-flex align-center">
                        <h6 class="text-subtitle-1 font-weight-bold mr-4">
                          {{ selectedPaper.title }}
                        </h6>
                        <v-btn-toggle v-model="isManualEditMode" mandatory dense>
                          <v-btn :value="false" small>
                            <v-icon small left>mdi-form-textbox</v-icon>
                            메타데이터
                          </v-btn>
                          <v-btn :value="true" small color="orange">
                            <v-icon small left>mdi-comment-edit-outline</v-icon>
                            주석 편집
                          </v-btn>
                        </v-btn-toggle>
                      </div>
                      <div class="d-flex">
                        <v-btn
                          color="primary"
                          outlined
                          class="mr-2"
                          @click="showPreviewModal = true"
                        >
                          {{ $t("label.preview") }}
                        </v-btn>
                        <v-btn color="success" @click="handleSave" :loading="isLoading">
                          <v-icon left size="18">mdi-content-save</v-icon>
                          {{ $t("button.save") }}
                        </v-btn>
                      </div>
                    </div>

                    <!-- 메타데이터 편집 모드 -->
                    <div v-if="!isManualEditMode" class="overflow-y-auto pa-4" style="height: calc(100% - 64px);">
                      <!-- 저자 정보 -->
                      <div v-if="selectedPaper.authors?.length > 0" class="mb-4">
                        <h6 class="text-subtitle-2 font-weight-medium mb-2">
                          {{ $t("label.author_info") }}
                        </h6>
                        <v-card
                          v-for="(author, index) in selectedPaper.authors"
                          :key="index"
                          class="mb-2 rounded-lg"
                          elevation="0"
                          outlined
                        >
                          <v-card-text class="pa-3">
                            <v-row dense>
                              <v-col cols="6" md="3">
                                <v-text-field
                                  v-model="author.full_name"
                                  :label="$t('label.author_full_name')"
                                  dense
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="6" md="3">
                                <v-text-field
                                  v-model="author.name_eng"
                                  :label="$t('label.author_name_eng')"
                                  dense
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="6" md="3">
                                <v-text-field
                                  v-model="author.organization"
                                  :label="$t('label.author_organization')"
                                  dense
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="6" md="3">
                                <v-text-field
                                  v-model="author.email"
                                  :label="$t('label.author_email')"
                                  dense
                                  hide-details="auto"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>

                      <v-divider class="mb-4"></v-divider>

                      <!-- 논문 정보 (메타데이터) -->
                      <v-row dense>
                        <v-col cols="12">
                          <v-text-field
                            v-model="selectedPaper.title"
                            :label="$t('label.title')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="selectedPaper.title_eng"
                            :label="$t('label.title_eng')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="selectedPaper.subtitle"
                            :label="$t('label.subtitle')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="selectedPaper.subtitle_eng"
                            :label="$t('label.subtitle_eng')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="titlePageCommentContent"
                            label="타이틀 각주"
                            rows="2"
                            hide-details="auto"
                            class="mb-3"
                            outlined
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="selectedPaper.keyword"
                            :label="$t('label.keyword')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="selectedPaper.keyword_eng"
                            :label="$t('label.keyword_eng')"
                            hide-details="auto"
                            class="mb-3"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="selectedPaper.abstract"
                            label="초록"
                            rows="4"
                            hide-details="auto"
                            class="mb-3"
                            outlined
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="selectedPaper.abstract_eng"
                            label="초록 (영문)"
                            rows="4"
                            hide-details="auto"
                            class="mb-3"
                            outlined
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="selectedPaper.references"
                            label="참고문헌"
                            rows="6"
                            hide-details="auto"
                            class="mb-3"
                            outlined
                            placeholder="참고문헌을 입력하세요. 각 항목은 줄바꿈으로 구분됩니다."
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- 주석 편집 모드 -->
                    <div v-else class="overflow-y-auto pa-4" style="height: calc(100% - 64px);">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div>
                          <h6 class="text-subtitle-1 font-weight-bold">주석 편집</h6>
                          <p class="text-caption grey--text mb-0">
                            페이지/첨자/내용을 수정하세요.
                          </p>
                        </div>
                        <v-btn color="green" small @click="addComment">
                          <v-icon small left>mdi-plus</v-icon>
                          주석 추가
                        </v-btn>
                      </div>
                      <v-alert type="info" text outlined class="mb-3">
                        본문에 주석 마커가 있으면 자동 배치되고, 마커가 없으면 페이지 번호를 2 이상으로 지정해야 표시됩니다.
                      </v-alert>
                      <v-card outlined class="rounded-lg">
                        <v-card-text>
                          <div v-if="nonTitleComments.length === 0" class="text-caption grey--text">
                            등록된 주석이 없습니다.
                          </div>
                          <v-card
                            v-for="(comment, index) in nonTitleComments"
                            :key="comment.idx || index"
                            class="mb-2 rounded-lg"
                            outlined
                          >
                            <v-card-text>
                              <div class="d-flex justify-space-between align-start mb-2">
                                <div class="d-flex flex-wrap">
                                  <v-text-field
                                    v-model="comment.page"
                                    label="페이지"
                                    type="number"
                                    dense
                                    outlined
                                    class="mr-2"
                                    style="max-width: 140px;"
                                  ></v-text-field>
                                  <v-text-field
                                    v-model="comment.superscript"
                                    label="첨자"
                                    dense
                                    outlined
                                    class="mr-2"
                                    style="max-width: 120px;"
                                  ></v-text-field>
                                </div>
                                <v-btn icon color="pink" @click="removeComment(comment)">
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </div>
                              <v-textarea
                                v-model="comment.content"
                                label="내용"
                                rows="3"
                                outlined
                              ></v-textarea>
                            </v-card-text>
                          </v-card>
                        </v-card-text>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 미리보기 모달 -->
    <v-dialog v-model="showPreviewModal" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">{{ $t("label.preview") }}</span>
          <v-btn icon @click="showPreviewModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6" style="max-height: 800px">
          <div class="d-flex justify-center align-center">
            <div ref="pdfContent">
              <component
                v-if="selectedPaper"
                :is="selectedPdfTemplate"
                :item="selectedPaper"
                :journal="journal"
                :startPageNumber="startPageNumber"
                :pageSize="journal?.page_size || 'A5'"
                :manualMode="isManualSplitMode"
                @breakpoints-calculated="onBreakpointsCalculated"
              />
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showPreviewModal = false">
            {{ $t("button.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 필수 페이지 미리보기 모달 -->
    <v-dialog v-model="showFixedPreviewModal" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">{{ selectedFixedPage?.title }} 미리보기</span>
          <v-btn icon @click="showFixedPreviewModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6 grey lighten-3" style="max-height: 800px">
          <div class="d-flex justify-center">
            <!-- 표지 미리보기 -->
            <article-cover-page
              v-if="selectedFixedPage?.id === 'cover'"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 정보페이지 미리보기 -->
            <article-info-page
              v-else-if="selectedFixedPage?.id === 'info'"
              :item="{}"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 속표지(앞) 미리보기 -->
            <article-inner-cover-front
              v-else-if="selectedFixedPage?.id === 'inner_front'"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 빈페이지 미리보기 -->
            <article-blank-page
              v-else-if="selectedFixedPage?.id === 'blank'"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 표제지 미리보기 -->
            <article-title-page
              v-else-if="selectedFixedPage?.id === 'title'"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 목차 미리보기 -->
            <article-table-of-contents
              v-else-if="selectedFixedPage?.id === 'toc'"
              :group="{}"
              :journal="previewJournal"
              :items="papers"
            />
            <!-- 속표지(뒷) 미리보기 -->
            <article-inner-cover-back
              v-else-if="selectedFixedPage?.id === 'inner_back'"
              :group="{}"
              :journal="previewJournal"
            />
            <!-- 백커버 미리보기 -->
            <article-committee-page
              v-else-if="selectedFixedPage?.id === 'backcover'"
              :group="{}"
              :journal="previewJournal"
              :items="papers"
            />
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showFixedPreviewModal = false">
            닫기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 전체 저널 미리보기 모달 -->
    <v-dialog v-model="showFullJournalPreview" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">전체 저널 미리보기</span>
          <v-btn icon @click="showFullJournalPreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6 grey lighten-3" style="max-height: 800px">
            <div ref="fullJournalPreview" class="final-preview-content">
              <full-journal-preview-content
                v-if="fullPdfPages.length > 0"
                :journal="journal"
                :page-size="pageSize"
                :pages="fullPdfPages"
                :items="fullPdfItems"
                :enabled-fixed-pages="isFixedPageEnabled"
              />
            </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showFullJournalPreview = false">
            닫기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </v-container>
</template>

<script>
/* eslint-disable no-console */
import ArticlePaperPage from "@/components/Templates/article/ArticlePaperPage.vue";
import AbstractPaperPage from "@/components/Templates/abstract/AbstractPaperPage.vue";
import ProceedingsPaperPage from "@/components/Templates/proceedings/ProceedingsPaperPage.vue";

import ArticleCoverPage from "@/components/Templates/article/ArticleCoverPage.vue";
import ArticleInfoPage from "@/components/Templates/article/ArticleInfoPage.vue";
import ArticleCommitteePage from "@/components/Templates/article/ArticleCommitteePage.vue";
import ArticleInnerCoverFront from "@/components/Templates/article/ArticleInnerCoverFront.vue";
import ArticleBlankPage from "@/components/Templates/article/ArticleBlankPage.vue";
import ArticleTitlePage from "@/components/Templates/article/ArticleTitlePage.vue";
import ArticleTableOfContents from "@/components/Templates/article/ArticleTableOfContents.vue";
import ArticleInnerCoverBack from "@/components/Templates/article/ArticleInnerCoverBack.vue";
import FullJournalPreviewContent from "@/components/Journal/FullJournalPreviewContent.vue";

import { journalContentApi, journalApi } from "@/api/api";

import { savePopup } from "@/fields/paper/popups";
// 변경완료 - 20260220 (미리보기 용도 Utils)
import {
  PAGE_DIMENSIONS,
  getContentWidth,
  getPagePadding,
  splitContentByHeight,
} from "@/utils/paperEditor";
import { openIframePrintPreview } from "@/utils/pdfGenerator";
import { parseContentToAst, renderAstToHtml } from "@/utils/paperAst";

// 변경완료 - 20260220 (이미지 URL 변환용 서버 URL)
const SERVER_URL = process.env.VUE_APP_SERVER_URI;


export default {
  name: "edit-group-papers",
  components: {
    FullJournalPreviewContent,
    ArticlePaperPage,
    AbstractPaperPage,
    ProceedingsPaperPage,
    ArticleCoverPage,
    ArticleInfoPage,
    ArticleCommitteePage,
    ArticleInnerCoverFront,
    ArticleBlankPage,
    ArticleTitlePage,
    ArticleTableOfContents,
    ArticleInnerCoverBack,
  },
  props: {
    journal_idx: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSavingSettings: false,
      papers: [],
      allContents: [], // 미리보기용 (공페이지 포함)
      journal: {},
      selectedPaper: null,
      selectedType: null, // 'fixed' or 'paper'
      selectedFixedPage: null,
      showPreviewModal: false,
      showFixedPreviewModal: false,
      showFullJournalPreview: false,
      // 변경완료 - 20260220 (미리보기 위한 상태)
      isFullPdfLoading: false,
      pageSize: "A5",
      fullPdfGroups: [],
      fullPdfPages: [],
      isBuilding: null,

      // 수동 페이지 편집 모드
      isManualEditMode: false,
      // 수동 분할 모드 (true: --- 마커에서만 페이지 나눔, false: 자동 높이 분할)
      isManualSplitMode: false,
      fixedPages: [
        {
          id: "cover",
          title: "표지",
          description: "저널 앞표지 정보",
          icon: "mdi-book-open-page-variant",
          color: "indigo",
          enabled: true,
        },
        {
          id: "info",
          title: "정보페이지",
          description: "임원/발행 정보",
          icon: "mdi-information",
          color: "teal",
          enabled: true,
        },
        {
          id: "inner_front",
          title: "속표지(앞)",
          description: "내지 앞표지",
          icon: "mdi-file-document-outline",
          color: "blue-grey",
          enabled: true,
        },
        {
          id: "blank",
          title: "빈페이지",
          description: "공페이지",
          icon: "mdi-file-outline",
          color: "grey",
          enabled: true,
        },
        {
          id: "title",
          title: "표제지",
          description: "편집위원 정보",
          icon: "mdi-account-group",
          color: "deep-purple",
          enabled: true,
        },
        {
          id: "toc",
          title: "목차",
          description: "CONTENTS",
          icon: "mdi-format-list-bulleted",
          color: "orange",
          enabled: true,
        },
        {
          id: "inner_back",
          title: "속표지(뒷)",
          description: "임원 정보",
          icon: "mdi-file-document",
          color: "blue-grey darken-2",
          enabled: true,
        },
        {
          id: "backcover",
          title: "백커버",
          description: "논문 제목/저자",
          icon: "mdi-book-open-variant",
          color: "brown",
          enabled: true,
        },
      ],
      coverData: {
        journal_title: "International Journal of Korean Applied Linguistics",
        journal_title_eng: "International Journal of Korean Applied Linguistics",
        issn: "2799-9106",
        society_name: "국제한국어응용언어학회",
        society_name_eng: "The International Society of Korean Applied Linguistics",
      },
      // 정보페이지 데이터 (한글 임원 정보)
      infoPageData: {
        advisor: "",
        advisory_committee: "",
        president: "",
        vice_president: "",
        secretary_general: "",
        general_affairs: "",
        research_planning: "",
        information: "",
        editing: "",
        public_information: "",
        international_cooperation: "",
        editorial_board_chair: "",
        editorial_board: "",
      },
      // 표제지 데이터 (Editor in Chief / Editorial Board)
      titlePageData: {
        editorial_board_chair: "",
        editorial_board: "",
      },
      // 속표지(뒷) 데이터 (영문 임원 정보)
      innerBackData: {
        advisory_committee: "",
        president: "",
        vice_president: "",
        secretary_general: "",
        general_affairs: "",
        research_planning: "",
        information: "",
        editing: "",
        public_information: "",
        public_relations: "",
        editorial_board: "",
      },
    };
  },
  computed: {
    selectedPdfTemplate() {
      if (this.journal?.journal_type?.code === "abst") {
        return "abstract-paper-page";
      } else if (this.journal?.journal_type?.code === "procs") {
        return "proceedings-paper-page";
      }
      return "article-paper-page";
    },
    startPageNumber() {
      if (this.journal?.journal_type?.code === "jnl") {
        return 2;
      }
      return 1;
    },
    previewJournal() {
      return {
        ...this.journal,
        cover_info: JSON.stringify(this.coverData),
        info_page_info: JSON.stringify(this.infoPageData),
        title_page_info: JSON.stringify(this.titlePageData),
        inner_back_info: JSON.stringify(this.innerBackData),
      };
    },
    // 미리보기용 논문 (현재 편집 중인 내용 반영)
    nonTitleComments() {
      if (!this.selectedPaper?.comments) return [];
      return this.selectedPaper.comments.filter(
        (comment) => !this.isTitlePageComment(comment)
      );
    },
    titlePageCommentContent: {
      get() {
        const comment = this.getTitlePageComment();
        return comment?.content || "";
      },
      set(value) {
        this.updateTitlePageComment(value);
      },
    },
    // 변경완료 - 20260220
    dimensions() {
      return PAGE_DIMENSIONS[this.pageSize];
    },
    fullPdfItems() {
      return this.fullPdfGroups.reduce((acc, group) => acc.concat(group.contents || []), []);
    },
  },
  methods: {
    /****************************************************************************************
     * 선택 액션
     ****************************************************************************************/
    selectFixedPage(fixedItem) {
      this.selectedType = "fixed";
      this.selectedFixedPage = fixedItem;
      this.selectedPaper = null;
    },

    selectPaper(content) {
      this.selectedType = "paper";
      this.selectedFixedPage = null;
      this.isManualEditMode = false; // 기본은 메타데이터 모드
      this.fetchPaperDetail(content.idx);
    },

    // 논문 상세 로드 후 수동 분할 모드 복원
    restoreManualSplitMode() {
      if (this.selectedPaper && this.selectedPaper.manual_split_mode !== undefined) {
        this.isManualSplitMode = this.selectedPaper.manual_split_mode === true || this.selectedPaper.manual_split_mode === 1;
      } else {
        this.isManualSplitMode = false;
      }
    },

    addComment() {
      if (!this.selectedPaper) return;
      if (!Array.isArray(this.selectedPaper.comments)) {
        this.selectedPaper.comments = [];
      }
      this.selectedPaper.comments.push({
        page: null,
        superscript: null,
        content: "",
      });
    },

    removeComment(comment) {
      if (!this.selectedPaper?.comments) return;
      const index = this.selectedPaper.comments.indexOf(comment);
      if (index >= 0) {
        this.selectedPaper.comments.splice(index, 1);
      }
    },

    formatAuthors(authors) {
      if (!authors || !Array.isArray(authors)) return '';
      return authors.map(a => a.full_name || a.name_eng || '').filter(Boolean).join(', ');
    },

    getTitlePageComment() {
      const comments = this.selectedPaper?.comments || [];
      return comments.find((comment) => this.isTitlePageComment(comment));
    },

    updateTitlePageComment(content) {
      if (!this.selectedPaper) return;
      if (!Array.isArray(this.selectedPaper.comments)) {
        this.selectedPaper.comments = [];
      }
      const trimmed = (content || "").trim();
      const comments = this.selectedPaper.comments;
      const existingIndex = comments.findIndex((comment) =>
        this.isTitlePageComment(comment)
      );
      if (!trimmed) {
        if (existingIndex >= 0) {
          comments.splice(existingIndex, 1);
        }
        return;
      }
      const nextComment = {
        page: 1,
        superscript: "*",
        content: trimmed,
      };
      if (existingIndex >= 0) {
        comments.splice(existingIndex, 1, {
          ...comments[existingIndex],
          ...nextComment,
        });
      } else {
        comments.push(nextComment);
      }
    },

    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },

    isFixedPageEnabled(pageId) {
      const page = this.fixedPages.find(p => p.id === pageId);
      return page ? page.enabled : true;
    },
    // 변경완료 - 20260220 (ShowFinalJournalContent 동일 로직 이식)
    async openFullJournalPreview() {
      this.isFullPdfLoading = true;
      try {
        await this.ensureFullPdfReady();
        const previewElement = this.$refs.fullJournalPreview;
        if (this.fullPdfPages.length === 0) {
          this.notifyVue({ message: "미리보기를 준비할 논문 데이터가 없습니다.", style: "warning" });
          return;
        }
        this.showFullJournalPreview = true;
      } catch (error) {
        console.error("Preview render failed:", error);
      } finally {
        this.isFullPdfLoading = false;
      }
    },
    async ensureFullPdfReady() {
      if (this.isBuilding) {
        await this.isBuilding;
        return;
      }
      if (this.fullPdfPages.length > 0) return;
      
      this.isBuilding = (async () => {
        await this.buildFullPdfGroups();
        this.buildFullPdfPages();
        await this.$nextTick();
      })();
      await this.isBuilding;
      this.isBuilding = null;
    },
    async buildFullPdfGroups() {
      // EditGroupPapers에서는 this.papers 가 전체 논문 목록이므로 이를 묶어서 처리
      this.fullPdfGroups = [{
        idx: 1, // 가상 idx
        title: this.journal?.title || "",
        contents: this.papers || [],
      }];
    },
    buildFullPdfPages() {
      const pages = [];
      let pageNumber = 1;

      this.fullPdfGroups.forEach((group, groupIndex) => {
        (group.contents || []).forEach((paper, paperIndex) => {
          const paperData = paper;
          const abstractHtml = this.buildAbstractHtml(paperData);
          const abstractPages = abstractHtml
            ? splitContentByHeight(
                abstractHtml,
                this.pageSize,
                this.getAbstractCoverHeightForPaper(paperData),
                {
                  containerStyle: 'font-family: "KoPubWorldBatang", "Noto Serif KR", serif; font-size: 10pt; line-height: 1.8; text-align: justify;',
                  paragraphStyle: "margin: 0 0 10px 0;",
                }
              )
            : [""];
          const contentHtml = this.processImageUrls(this.normalizeContentToHtml(paperData?.content));
          const contentPages = contentHtml ? splitContentByHeight(contentHtml, this.pageSize, 0) : [""];
          const splitComments = this.splitCommentsForPaper(paperData, contentPages);

          if (pageNumber % 2 === 0) {
            pages.push({
              key: `paper-${paperData.idx}-blank-${pageNumber}`,
              type: "blank",
              paper: paperData,
              group,
              pageNumber,
            });
            pageNumber += 1;
          }

          pages.push({
            key: `paper-${paperData.idx}-cover`,
            type: "paper-cover",
            paper: paperData,
            group,
            comments: this.getCoverCommentsForPaper(paperData),
            pageNumber,
          });
          pageNumber += 1;

          abstractPages.forEach((pageHtml, index) => {
            pages.push({
              key: `paper-${paperData.idx}-abstract-${index}`,
              type: "abstract",
              paper: paperData,
              group,
              pageNumber,
              html: pageHtml,
              isFirst: index === 0,
              isLast: index === abstractPages.length - 1,
            });
            pageNumber += 1;
          });

          contentPages.forEach((pageHtml, index) => {
            pages.push({
              key: `paper-${paperData.idx}-content-${index}`,
              type: "content",
              paper: paperData,
              group,
              pageNumber,
              html: pageHtml,
              comments: splitComments[index] || [],
            });
            pageNumber += 1;
          });
        });
      });

      if (pages.length > 0) {
        const backPagesCount = ["inner_back", "backcover"].reduce((count, pageId) => (
          count + (this.isFixedPageEnabled(pageId) ? 1 : 0)
        ), 0);
        const lastPageNumber = pageNumber - 1 + backPagesCount;
        if (lastPageNumber % 2 !== 0) {
          pages.push({
            key: `final-blank-${pageNumber}`,
            type: "blank",
            paper: {},
            group: {},
            pageNumber,
          });
          pageNumber += 1;
        }
      }

      this.fullPdfPages = pages;
    },
    buildAbstractHtml(paper) {
      const source = paper?.abstract || paper?.abstract_eng || "";
      if (!source) return "";
      return this.processImageUrls(renderAstToHtml(parseContentToAst(source)));
    },
    processImageUrls(htmlString, { addCrossOrigin = false, preferRelative = false } = {}) {
      if (!htmlString) return htmlString;
      const replaceIskalPath = (match, quote, path) => {
        const normalized = path.startsWith("/") ? path.slice(1) : path;
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      const replaceStorageUrl = (match, quote, path) => {
        const normalized = path.replace(/^\/?storage\//, "");
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      let withAbsolute = htmlString
        .replace(/src=(["'])(\/?iskal\/[^"']+)\1/g, replaceIskalPath)
        .replace(/src=(["'])(https?:\/\/[^"']+\/storage\/[^"']+)\1/g, (match, quote, url) => {
          const [, path = ""] = url.split("/storage/");
          return replaceStorageUrl(match, quote, `/storage/${path}`);
        })
        .replace(/src=(["'])(\/?storage\/[^"']+)\1/g, replaceStorageUrl);
      if (!addCrossOrigin) return withAbsolute;
      return withAbsolute.replace(/<img(?![^>]*\scrossorigin=)/g, '<img crossorigin="anonymous"');
    },
    normalizeContentToHtml(rawContent) {
      if (!rawContent) return "";
      if (typeof rawContent !== "string") {
        return rawContent;
      }
      const trimmed = rawContent.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
            return renderAstToHtml(parsed);
          }
        } catch (error) {
          console.warn("content JSON parse failed", error);
        }
      }
      return rawContent;
    },
    getAbstractCoverHeightForPaper(paper) {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
      }
      const contentWidth = getContentWidth(this.pageSize, true);
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: ${contentWidth}px;
        padding: 0;
      `;

      const titleEl = document.createElement("div");
      titleEl.textContent = "● 초록(Abstract)";
      titleEl.style.cssText = `
        font-size: 12pt;
        font-weight: bold;
        margin: 0 0 16px 0;
      `;
      container.appendChild(titleEl);

      let keywordReserve = 0;
      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = document.createElement("div");
        keywordWrap.style.cssText = `
          font-size: 9pt;
          line-height: 1.4;
        `;
        const keywordLabel = document.createElement("span");
        keywordLabel.textContent = "핵심어";
        keywordLabel.style.cssText = `
          display: inline-block;
          padding: 2px 8px;
          font-size: 9pt;
        `;
        const keywordText = document.createElement("span");
        keywordText.textContent = paper.keyword || paper.keyword_eng || "";
        keywordText.style.cssText = "font-size: 9pt;";
        keywordWrap.appendChild(keywordLabel);
        keywordWrap.appendChild(keywordText);
        container.appendChild(keywordWrap);
      }

      document.body.appendChild(container);
      const titleStyles = window.getComputedStyle(titleEl);
      const titleHeight =
        titleEl.offsetHeight +
        (parseFloat(titleStyles.marginTop) || 0) +
        (parseFloat(titleStyles.marginBottom) || 0);

      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = container.lastChild;
        const keywordHeight = keywordWrap ? keywordWrap.offsetHeight : 0;
        const keywordBottom = 60;
        keywordReserve = keywordHeight + keywordBottom;
      }

      document.body.removeChild(container);
      return Math.ceil(titleHeight + keywordReserve);
    },
    splitCommentsForPaper(paper, contentPages) {
      const comments = paper?.comments || [];
      if (!comments.length || !contentPages.length) {
        return contentPages.map(() => []);
      }
      const result = contentPages.map(() => []);
      contentPages.forEach((pageContent, pageIndex) => {
        const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>/g;
        let match;
        while ((match = markerRegex.exec(pageContent)) !== null) {
          const footnoteId = match[1] || match[2];
          const comment = comments.find(c => {
            const num = c.superscript?.toString().replace(/\D/g, "");
            return num === footnoteId;
          });
          if (comment && !result[pageIndex].includes(comment)) {
            result[pageIndex].push(comment);
          }
        }
      });
      return result;
    },
    getCoverCommentsForPaper(paper) {
      const comments = paper?.comments || [];
      if (!comments.length) return [];
      const coverTextParts = [
        paper?.title,
        paper?.title_eng,
      ];
      (paper?.authors || []).forEach((author) => {
        coverTextParts.push(author?.full_name);
        coverTextParts.push(author?.name_eng);
        coverTextParts.push(author?.organization);
        coverTextParts.push(author?.organization_eng);
      });
      const coverText = coverTextParts.filter(Boolean).join(" ");
      const markerComments = this.collectCommentsByMarkers(coverText, comments);
      const titlePageComments = comments.filter((comment) =>
        this.isTitlePageComment(comment)
      );
      if (!titlePageComments.length) return markerComments;
      const combined = [...markerComments];
      titlePageComments.forEach((comment) => {
        if (!combined.includes(comment)) {
          combined.push(comment);
        }
      });
      return combined;
    },
    collectCommentsByMarkers(text, comments) {
      if (!text || !comments?.length) return [];
      const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>|\^(\d+)/g;
      const result = [];
      const used = new Set();
      let match;
      while ((match = markerRegex.exec(text)) !== null) {
        const footnoteId = match[1] || match[2] || match[3];
        const comment = comments.find((c) => {
          const num = c.superscript?.toString().replace(/\D/g, "");
          return num === footnoteId;
        });
        if (comment && !used.has(comment.idx)) {
          used.add(comment.idx);
          result.push(comment);
        }
      }
      return result;
    },

    /****************************************************************************************
     * 저장 액션
     ****************************************************************************************/
    async handleSave() {
      const isConfirmed = await savePopup();

      if (!isConfirmed) {
        return;
      }

      await this.updateJournalContent();
    },

    async handleSaveJournalInfo() {
      this.isLoading = true;
      try {
        const data = {
          idx: this.journal_idx,
          cover_info: JSON.stringify(this.coverData),
        };
        const response = await journalApi.updateJournalCover(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "저장되었습니다.",
            style: "success",
          });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleSaveInfoPage() {
      this.isLoading = true;
      try {
        const data = {
          idx: this.journal_idx,
          info_page_info: JSON.stringify(this.infoPageData),
        };
        const response = await journalApi.updateJournalInfoPage(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "정보페이지가 저장되었습니다.",
            style: "success",
          });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleSaveTitlePage() {
      this.isLoading = true;
      try {
        const data = {
          idx: this.journal_idx,
          title_page_info: JSON.stringify(this.titlePageData),
        };
        const response = await journalApi.updateJournalTitlePage(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "표제지가 저장되었습니다.",
            style: "success",
          });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleSaveInnerBack() {
      this.isLoading = true;
      try {
        const data = {
          idx: this.journal_idx,
          inner_back_info: JSON.stringify(this.innerBackData),
        };
        const response = await journalApi.updateJournalInnerBack(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "속표지(뒷)가 저장되었습니다.",
            style: "success",
          });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async saveFixedPagesSettings() {
      this.isSavingSettings = true;
      try {
        // 기본 on/off 설정 객체
        const settings = {};
        this.fixedPages.forEach((page) => {
          settings[page.id] = page.enabled;
        });

        // 기존 fixed_pages_info가 배열이면 배열 형식으로 저장 (페이지 구성 유지)
        let payload = settings;
        try {
          if (this.journal?.fixed_pages_info) {
            const parsed = JSON.parse(this.journal.fixed_pages_info);
            if (Array.isArray(parsed)) {
              const blankEnabled = settings.blank !== false;
              payload = parsed.filter((page) => {
                if (page?.id === "blank" || page?.isBlank) {
                  return blankEnabled;
                }
                if (!page?.id) return false;
                return settings[page.id] !== false;
              });
            }
          }
        } catch (e) {
          // 파싱 실패시 객체 저장으로 폴백
        }

        const data = {
          idx: this.journal_idx,
          fixed_pages_info: JSON.stringify(payload),
        };
        const response = await journalApi.updateJournalFixedPages(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "필수 페이지 설정이 저장되었습니다.",
            style: "success",
          });
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "설정 저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isSavingSettings = false;
      }
    },

    /****************************************************************************************
     * API
     ****************************************************************************************/
    async fetchJournal() {
      try {
        const params = {
          idx: this.journal_idx,
        };
        const response = await journalApi.showJournal(params);
        if (response.status === 200) {
          this.journal = response.data.response;
          // cover_info 파싱
          if (this.journal.cover_info) {
            try {
              this.coverData = {
                ...this.coverData,
                ...JSON.parse(this.journal.cover_info),
              };
            } catch (e) {
              console.warn("cover_info 파싱 실패", e);
            }
          }
          // info_page_info 파싱 (정보페이지)
          if (this.journal.info_page_info) {
            try {
              this.infoPageData = {
                ...this.infoPageData,
                ...JSON.parse(this.journal.info_page_info),
              };
            } catch (e) {
              console.warn("info_page_info 파싱 실패", e);
            }
          }
          // title_page_info 파싱 (표제지)
          if (this.journal.title_page_info) {
            try {
              this.titlePageData = {
                ...this.titlePageData,
                ...JSON.parse(this.journal.title_page_info),
              };
            } catch (e) {
              console.warn("title_page_info 파싱 실패", e);
            }
          }
          // inner_back_info 파싱 (속표지 뒷)
          if (this.journal.inner_back_info) {
            try {
              this.innerBackData = {
                ...this.innerBackData,
                ...JSON.parse(this.journal.inner_back_info),
              };
            } catch (e) {
              console.warn("inner_back_info 파싱 실패", e);
            }
          }
          // fixed_pages_info 파싱 (필수 페이지 on/off 설정)
          if (this.journal.fixed_pages_info) {
            try {
              const parsed = JSON.parse(this.journal.fixed_pages_info);
              // 객체 형식 { id: enabled }
              if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                this.fixedPages.forEach((page) => {
                  if (Object.prototype.hasOwnProperty.call(parsed, page.id)) {
                    page.enabled = parsed[page.id];
                  }
                });
              }
              // 배열 형식 [{ id, title, page, isBlank }]
              else if (Array.isArray(parsed)) {
                const ids = new Set(parsed.map((page) => page?.id).filter(Boolean));
                const hasBlank = parsed.some((page) => page?.id === "blank" || page?.isBlank);
                const frontPageIds = new Set(["cover", "info", "inner_front", "blank", "title", "toc"]);
                this.fixedPages.forEach((page) => {
                  if (page.id === "blank") {
                    page.enabled = hasBlank;
                    return;
                  }
                  if (frontPageIds.has(page.id)) {
                    page.enabled = ids.has(page.id);
                    return;
                  }
                  // 배열에는 뒷쪽 고정 페이지가 없으므로 기본값 유지
                  page.enabled = true;
                });
              }
            } catch (e) {
              console.warn("fixed_pages_info 파싱 실패", e);
            }
          }
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },

    async fetchPapers() {
      try {
        this.isLoading = true;
        const params = {
          journal_idx: this.journal_idx,
        };
        const response = await journalContentApi.showAll(params);
        if (response.status === 200) {
          const allPapers = response.data.response?.elements || response.data.response || [];
          // 전체 콘텐츠 저장 (미리보기용 - 공페이지 포함)
          this.allContents = allPapers;
          // 공페이지 필터링 (페이지 수 조정용 빈 페이지는 논문이 아님)
          this.papers = allPapers.filter(paper =>
            paper.category !== "empty" &&
            paper.title !== "공페이지" &&
            paper.title !== "빈페이지"
          );
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPaperDetail(idx, forceManualMode = null) {
      try {
        this.isLoading = true;
        const response = await journalContentApi.showJournalContent({
          idx: idx,
        });

        if (response.status === 200) {
          const paperData = response.data.response;
          // 저장 후 호출 시: manual_split_mode를 미리 설정 (Vue 반응성 업데이트 전에)
          if (forceManualMode !== null) {
            paperData.manual_split_mode = forceManualMode;
            this.isManualSplitMode = forceManualMode;
          }
          this.selectedPaper = paperData;
          // 일반 호출 시: 저장된 수동 분할 모드 복원
          if (forceManualMode === null) {
            this.restoreManualSplitMode();
          }
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    // breakpoints 계산 완료 시 selectedPaper에 저장
    onBreakpointsCalculated({ page_breakpoints, breakpoints_meta }) {
      if (!this.selectedPaper) return;

      // selectedPaper에 breakpoints 정보 추가
      this.selectedPaper.page_breakpoints = JSON.stringify(page_breakpoints);
      this.selectedPaper.breakpoints_meta = JSON.stringify(breakpoints_meta);

      console.log('[Breakpoints] Calculated:', {
        page_breakpoints,
        breakpoints_meta,
      });
    },

    async updateJournalContent() {
      try {
        // 수동 분할 모드 상태 저장
        this.selectedPaper.manual_split_mode = this.isManualSplitMode;
        // 저장 전 현재 모드 보존 (fetchPaperDetail 후 복원용)
        const savedManualSplitMode = this.isManualSplitMode;

        const response = await journalContentApi.updateJournalContent(
          this.selectedPaper
        );

        if (response.status === 200) {
          this.notifyVue({
            message: this.$t("alert.success_request"),
            style: "success",
          });
          // 논문 목록 새로고침
          await this.fetchPapers();
          // 현재 선택된 논문 정보 새로고침 (저장된 수동 모드 유지)
          await this.fetchPaperDetail(this.selectedPaper.idx, savedManualSplitMode);
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },

    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
      timeout = 5000,
    } = {}) {
      this.$notify({
        timeout: timeout,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  async mounted() {
    await this.fetchJournal();
    await this.fetchPapers();
  },
};
</script>

<style scoped>
.fill-height {
  height: 100%;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
